import { AlertDialog, BodyText, Collapsible, toast } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { MedicalHistoryForm } from "@/features/Patient/MedicalRecords/MedicalHistory/components/MedicalHistoryForm";
import { useUpdateEncounterEpisodeOfCare } from "@/features/Patient/MedicalRecords/MedicalHistory/network";
import type { medicalHistoryFormSchema } from "@/features/Patient/MedicalRecords/MedicalHistory/schemas";
import { trackEvent } from "@/lib/tracking/tracking";
import { isActionAvailable } from "@/utils/available-actions";
import { Condition, Encounter, getDisplay } from "@curai/js-fhir-tools";
import { useParams, useSearch } from "@tanstack/react-router";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import type { z } from "zod";
import type { EpisodeOfCareResponse } from "../network/useEpisodesOfCare";
import { useReassignEpisodeOfCare } from "../network/useReassignEpisodeOfCare";
import { Assessment } from "./Assessment/Assessment";
import { CarePlan } from "./CarePlan/CarePlan";
import { AssessmentAction, useAssessment } from "./network/useAssessment";
import {
  CareplanAvailableAction,
  useCarePlanSummary,
} from "./network/useCarePlanSummary";
import {
  ChartingAvailableAction,
  useChartingAvailableActions,
} from "./network/useChartingAvailableActions";
import { useDeleteAssessment } from "./network/useDeleteAssessment";
import { useDeleteCarePlan } from "./network/useDeleteCarePlan";
interface EpisodeOfCareProps {
  episodeOfCare: EpisodeOfCareResponse["episodeOfCareResources"][0];
}

enum ConfirmActionType {
  DELETE_ASSESSMENT = "deleteAssessment",
  DELETE_CARE_PLAN = "deleteCarePlan",
}

export const EpisodeOfCare = ({ episodeOfCare }: EpisodeOfCareProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const searchParams = useSearch({ from: "/patient/$patientId" });
  const motherEncounterId = searchParams.encounterId as string; //  `Chart` is conditionally rendered by presence of encounterId

  const [open, setOpen] = useState(true);
  const [isEocOpenedInEditingState, setIsEocOpenedInEditingState] =
    useState(false);

  const { condition } = episodeOfCare;
  const conditionModel = new Condition(condition); // TODO: Fix this
  const name = conditionModel.code
    ? getDisplay(conditionModel.code) || "Unknown EOC"
    : "Unknown EOC";

  const encounterId = episodeOfCare.childEncounter?.id || "";
  const childEncounter = new Encounter(episodeOfCare.childEncounter);
  const isAmazon = Boolean(childEncounter.amazonId);
  const { data: careplanSummary } = useCarePlanSummary(encounterId);

  const { data: chartingAvailableActions } = useChartingAvailableActions({
    patientId,
    motherEncounterId,
  });

  const { mutateAsync: deleteCarePlan, isPending: isDeletingCarePlan } =
    useDeleteCarePlan({
      patientId,
      motherEncounterId,
      encounterId,
      onError: () => {
        toast.error("Failed to delete care plan. Please try again.");
      },
    });

  const {
    mutateAsync: updateEncounterEpisodeOfCare,
    isPending: isUpdatingEncounterEpisodeOfCare,
  } = useUpdateEncounterEpisodeOfCare({
    patientId,
    motherEncounterId,
    episodeOfCareId: episodeOfCare.episodeOfCare?.id as string,
  });

  const {
    mutateAsync: reassignEpisodeOfCare,
    isPending: isReassigningEpisodeOfCare,
  } = useReassignEpisodeOfCare({
    patientId,
    motherEncounterId,
    childEncounterId: encounterId,
  });

  const { data: assessmentData } = useAssessment(encounterId);
  const { mutateAsync: deleteAssessment, isPending: isDeletingAssessment } =
    useDeleteAssessment({
      patientId,
      motherEncounterId,
      encounterId,
      docRefId: assessmentData?.assessment?.id,
      onError: () => {
        toast.error("Failed to delete assessment. Please try again.");
      },
    });
  const summary = careplanSummary?.summary;
  const careplanAvailableActions = careplanSummary?.availableActions;

  const isVoidCarePlanAllowed = isActionAvailable(
    CareplanAvailableAction.VOID_CARE_PLAN,
    careplanAvailableActions,
  );

  const canVoidCarePlan = Boolean(summary?.careplanId) && isVoidCarePlanAllowed;

  const [showActionPrompt, setShowActionPrompt] = useState(false);
  const [actionPromptMessage, setActionPromptMessage] = useState("");
  const [confirmActionType, setConfirmActionType] =
    useState<ConfirmActionType | null>(null);

  const canVoidAssessment =
    assessmentData?.availableActions?.availableActions.some(
      ({ action, unavailableReason }) =>
        action === AssessmentAction.VOID && !unavailableReason,
    );

  const handleClickConfirmAction = () => {
    if (confirmActionType === ConfirmActionType.DELETE_ASSESSMENT) {
      deleteAssessment();
    } else if (confirmActionType === ConfirmActionType.DELETE_CARE_PLAN) {
      deleteCarePlan();
    }

    setShowActionPrompt(false);
    setActionPromptMessage("");
    setConfirmActionType(null);
  };

  const handleClickConfirmCancel = () => {
    setShowActionPrompt(false);
    setActionPromptMessage("");
    setConfirmActionType(null);
  };

  const handleClickDeleteAssessment = () => {
    setShowActionPrompt(true);
    setActionPromptMessage("Are you sure you want to delete this assessment?");
    setConfirmActionType(ConfirmActionType.DELETE_ASSESSMENT);
  };

  const handleClickDeleteCarePlan = () => {
    setShowActionPrompt(true);
    setActionPromptMessage("Are you sure you want to delete this care plan?");
    setConfirmActionType(ConfirmActionType.DELETE_CARE_PLAN);
  };

  const handleSubmitConditionUpdate = async (
    data: z.infer<typeof medicalHistoryFormSchema>,
  ) => {
    try {
      if (data.existingEpisodeOfCareId) {
        await reassignEpisodeOfCare({
          targetEpisodeOfCareId: data.existingEpisodeOfCareId,
        });
      } else {
        await updateEncounterEpisodeOfCare({
          conditionCode: {
            coding: [
              {
                system: "http://snomed.info/sct",
                code: data.conditionCode,
                display: data.conditionCodeDisplay,
              },
            ],
            text: data.conditionCodeDisplay,
          },
        });
      }

      setIsEocOpenedInEditingState(false);
      toast.success("Updated EOC");
    } catch (error) {
      toast.error("Failed to update EOC. Please try again.");
    }
  };

  const forcedDropdownActions = [];

  const canEditCondition = isActionAvailable(
    ChartingAvailableAction.WRITE_EPISODE_OF_CARE,
    chartingAvailableActions,
  );

  forcedDropdownActions.push({
    label: "Change EOC",
    onClick: () => {
      setIsEocOpenedInEditingState(true);
      setOpen(true);
    },
    icon: <Pencil className="w-3.5" data-testid="edit-eoc" />,
    disabled: !canEditCondition || isEocOpenedInEditingState,
  });

  forcedDropdownActions.push({
    label: "Void Care Plan",
    onClick: handleClickDeleteCarePlan,
    icon: <Trash2 className="w-3.5" data-testid="void-button" />,
    disabled: isDeletingCarePlan || isDeletingAssessment || !canVoidCarePlan,
    variant: "danger" as const,
  });

  forcedDropdownActions.push({
    label: "Void Assessment",
    onClick: handleClickDeleteAssessment,
    icon: <Trash2 className="w-3.5" data-testid="void-button" />,
    disabled: isDeletingAssessment || isDeletingCarePlan || !canVoidAssessment,
    variant: "danger" as const,
  });

  const handleOpenChange = (open: boolean) => {
    trackEvent(open ? TRACKERS.EXPAND_EOC : TRACKERS.COLLAPSE_EOC);
    setOpen(open);
    if (!open) {
      // reset the editing state when the collapsible is closed, if the user re-opens the collapsible
      // without clicking the edit button, the Condition edit form should not be shown
      setIsEocOpenedInEditingState(false);
    }
  };

  return (
    <>
      <Collapsible
        data-testid="episode-of-care"
        onOpenChange={handleOpenChange}
        title={`(EOC) ${name}`}
        controlled
        open={open}
        forcedDropdownActions={forcedDropdownActions}
        dropdownActionsLoading={isDeletingAssessment || isDeletingCarePlan}
      >
        <div className="flex flex-col gap-2">
          {/* Reusing MedicalHistoryForm for expediency but it'd be kinda nice to have a dedicated
        Encounter Charting form for this — but technically it operates the exact same way. */}
          {isEocOpenedInEditingState && (
            <MedicalHistoryForm
              patientId={patientId}
              medicalHistory={{
                episodeOfCare: episodeOfCare.episodeOfCare,
                condition: conditionModel,
                lastVisit: null,
              }}
              onSubmit={handleSubmitConditionUpdate}
              isSubmitting={
                isUpdatingEncounterEpisodeOfCare || isReassigningEpisodeOfCare
              }
              includeExistingEpisodes={true}
            />
          )}
          <BodyText size="sm" textColor="primary" bold>
            Assessment
          </BodyText>
          <Assessment encounterId={encounterId} />

          <CarePlan
            patientId={patientId}
            encounterId={encounterId}
            motherEncounterId={motherEncounterId}
            condition={condition}
            episodeOfCareId={episodeOfCare.episodeOfCare?.id}
            isAmazon={isAmazon}
          />
        </div>
      </Collapsible>
      <AlertDialog
        isOpen={showActionPrompt}
        onOpenChange={setShowActionPrompt}
        isControlled
        title={"Confirm Action"}
        body={actionPromptMessage}
        actionLabel="Yes"
        cancelLabel="No"
        onAction={handleClickConfirmAction}
        onCancel={handleClickConfirmCancel}
        // TODO: Remove this once we fix the AlertDialogProvider
        // biome-ignore lint/complexity/noUselessFragments: We need to pass a trigger element to the AlertDialog so the AlertDialogProvider doesn't render the default trigger element
        triggerElement={<></>}
      />
    </>
  );
};
